import { useCallback, useMemo } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { authConfigured, loginRequest, tokenRequest } from './msalConfig';

export type WicnicRole = 'WICnic.Admin' | 'WICnic.Editor' | 'WICnic.Attendee';

interface UseAuth {
  /** True when MSAL has at least one signed-in account. */
  isSignedIn: boolean;
  /** True when Entra config is fully present (i.e. sign-in actually works). */
  authConfigured: boolean;
  /** Active account, or null. */
  account: AccountInfo | null;
  /** Display name from the token, falls back to UPN. */
  displayName: string | null;
  /** Roles from the `roles` claim in the ID token. */
  roles: WicnicRole[];
  /** Convenience role checks. */
  isAdmin: boolean;
  isEditor: boolean;
  isAttendee: boolean;
  /** Trigger sign-in (redirect flow). No-op + warning when auth is unconfigured. */
  signIn: () => Promise<void>;
  /** Sign out and redirect to home. */
  signOut: () => Promise<void>;
  /** Acquire an API access token (silent, with redirect fallback). */
  getApiToken: () => Promise<string | null>;
}

export function useAuth(): UseAuth {
  const { instance, accounts } = useMsal();
  const isSignedIn = useIsAuthenticated();
  const account = accounts[0] ?? null;

  const roles = useMemo<WicnicRole[]>(() => {
    const claims = account?.idTokenClaims as { roles?: string[] } | undefined;
    const raw = claims?.roles ?? [];
    return raw.filter(isWicnicRole);
  }, [account]);

  const displayName = useMemo<string | null>(() => {
    if (!account) return null;
    return account.name ?? account.username ?? null;
  }, [account]);

  const signIn = useCallback(async () => {
    if (!authConfigured) {
      // eslint-disable-next-line no-alert
      alert('Sign-in is not configured yet — Entra app registration pending.');
      return;
    }
    await instance.loginRedirect({
      ...loginRequest,
      // Sign-in from Home lands on My RSVP — the day-of hub (issue #49).
      // Sign-in started from any other page returns to that page as before.
      redirectStartPage: window.location.pathname === '/' ? '/my-rsvp' : window.location.href,
    });
  }, [instance]);

  const signOut = useCallback(async () => {
    if (!isSignedIn) return;
    await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance, isSignedIn]);

  const getApiToken = useCallback(async (): Promise<string | null> => {
    if (!authConfigured || !account) return null;
    try {
      const result = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        try {
          const result = await instance.acquireTokenPopup({ ...tokenRequest, account });
          return result.accessToken;
        } catch {
          return null;
        }
      }
      console.error('[useAuth] token acquisition failed', err);
      return null;
    }
  }, [account, instance]);

  return {
    isSignedIn,
    authConfigured,
    account,
    displayName,
    roles,
    isAdmin: roles.includes('WICnic.Admin'),
    isEditor: roles.includes('WICnic.Editor') || roles.includes('WICnic.Admin'),
    isAttendee: roles.includes('WICnic.Attendee') || roles.includes('WICnic.Editor') || roles.includes('WICnic.Admin'),
    signIn,
    signOut,
    getApiToken,
  };
}

function isWicnicRole(s: string): s is WicnicRole {
  return s === 'WICnic.Admin' || s === 'WICnic.Editor' || s === 'WICnic.Attendee';
}
